// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Paginasi Tahap 1) — pager bersama untuk daftar dokumen V2.
// protected: NO
// =============================================================================
// Pager bersama: "Menampilkan x–y dari N   [25 ▾]   ‹‹ ‹ p/pages › ››".
// - Penghitung SELALU tampil (bagian terpenting).
// - Saat memuat: pager tetap terlihat, tombol dinonaktifkan (tidak hilang).
// - Daftar kosong (total 0 & tidak memuat): tampilkan "Belum ada data", BUKAN pager kosong.
// - Mobile: cukup penghitung + dua panah (first/last & selektor ukuran disembunyikan).
import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export const PAGE_SIZES = [25, 50, 100];
const LS_PREFIX = "etams_v2_pagesize:";

// Baca ukuran halaman tersimpan per pengguna (dibungkus try/catch).
export const loadStoredPageSize = (key, def = 25) => {
  try {
    const v = parseInt(localStorage.getItem(LS_PREFIX + key), 10);
    return PAGE_SIZES.includes(v) ? v : def;
  } catch { return def; }
};
const storePageSize = (key, size) => {
  try { localStorage.setItem(LS_PREFIX + key, String(size)); } catch { /* localStorage gagal -> abaikan */ }
};

const idn = (n) => Number(n || 0).toLocaleString("id-ID");

export const ListPager = ({
  page, pageSize, total, pages, loading = false,
  onPageChange, onPageSizeChange, storageKey = "default", testid = "list-pager",
}) => {
  // Daftar kosong -> pesan, bukan pager kosong.
  if (total === 0 && !loading) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        Belum ada data
      </div>
    );
  }
  const totalPages = Math.max(1, pages || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (p) => { if (!loading && p >= 1 && p <= totalPages && p !== page) onPageChange(p); };
  const changeSize = (val) => {
    const s = parseInt(val, 10);
    if (!PAGE_SIZES.includes(s)) return;
    storePageSize(storageKey, s);
    onPageSizeChange(s);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3" data-testid={testid}>
      <div className="text-sm text-muted-foreground" data-testid={`${testid}-counter`}>
        Menampilkan <b className="text-foreground">{idn(from)}</b>–<b className="text-foreground">{idn(to)}</b>
        {" "}dari <b className="text-foreground">{idn(total)}</b>
      </div>

      <div className="flex items-center gap-2">
        {/* Selektor baris per halaman — disembunyikan di mobile */}
        <div className="hidden sm:block">
          <Select value={String(pageSize)} onValueChange={changeSize} disabled={loading}>
            <SelectTrigger className="h-8 w-[104px]" data-testid={`${testid}-size`} aria-label="Baris per halaman">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((s) => (
                <SelectItem key={s} value={String(s)} data-testid={`${testid}-size-${s}`}>{s} / hal</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8 hidden sm:inline-flex" aria-label="Halaman pertama"
                  disabled={loading || page <= 1} onClick={() => go(1)} data-testid={`${testid}-first`}>
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Halaman sebelumnya"
                  disabled={loading || page <= 1} onClick={() => go(page - 1)} data-testid={`${testid}-prev`}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-2 text-sm tabular-nums" aria-current="page" data-testid={`${testid}-current`}>
            {idn(page)}/{idn(totalPages)}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Halaman berikutnya"
                  disabled={loading || page >= totalPages} onClick={() => go(page + 1)} data-testid={`${testid}-next`}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 hidden sm:inline-flex" aria-label="Halaman terakhir"
                  disabled={loading || page >= totalPages} onClick={() => go(totalPages)} data-testid={`${testid}-last`}>
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
